// The companies counted for one customer in its current contract year: the
// drill-down under a row of the Nordic lookups usage page (ENG2-2465). Loaded
// only while the row is open; paged and searched (on local id) server-side.

import React, { useEffect, useState } from 'react'
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useNordicLookupWindows } from 'services/queries/use-nordic-lookup-windows'
import { NordicLookupSource } from 'services/cm-api/get-nordic-lookup-windows'
import {
  formatDate,
  formatDateTime,
  formatNumber,
  isCarriedOver,
  sourceLabel,
} from './helpers'

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]
const SEARCH_DEBOUNCE_MS = 400

const SOURCE_COLOUR: Record<
  NordicLookupSource,
  'default' | 'info' | 'warning'
> = {
  live: 'default',
  monitoring: 'info',
  backfill: 'warning',
}

const headSx = {
  fontSize: 12,
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  color: 'text.secondary',
  whiteSpace: 'nowrap' as const,
}

type Props = {
  customerId: number
  open: boolean
}

const CustomerLookups: React.FC<Props> = ({ customerId, open }) => {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput])

  const { data: response, isLoading } = useNordicLookupWindows(
    customerId,
    { page, pageSize, q: search || undefined },
    { enabled: open },
  )

  if (!open) return null

  const windows = response && response.success ? response.data : null

  return (
    <Box
      sx={{ px: { xs: 2, sm: 7 }, py: 2, bgcolor: 'action.hover' }}
      data-testid={`nordic-lookups-${customerId}`}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          mb: 1,
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
          Companies counted this period
        </Typography>
        <TextField
          size="small"
          placeholder="Search by local id…"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          sx={{ width: { xs: '100%', sm: 220 } }}
          inputProps={{ 'aria-label': 'Search counted companies by local id' }}
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : !windows ? (
        <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 2 }}>
          Could not load the companies counted for this customer.
        </Typography>
      ) : (
        <>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>Company</TableCell>
                  <TableCell sx={headSx}>First looked up</TableCell>
                  <TableCell sx={headSx}>Last looked up</TableCell>
                  <TableCell sx={headSx} align="right">
                    Requests this period
                  </TableCell>
                  <TableCell sx={headSx}>Counted by</TableCell>
                  <TableCell sx={headSx}>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {windows.lookups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography
                        sx={{ fontSize: 13, color: 'text.secondary', py: 1 }}
                      >
                        {search
                          ? 'No counted company matches the search'
                          : 'No companies counted this period yet'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  windows.lookups.map((lookup) => (
                    <TableRow key={`${lookup.country}-${lookup.local_id}`}>
                      <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                        {lookup.country} · {lookup.local_id}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                        {isCarriedOver(lookup)
                          ? `Monitored since ${formatDate(
                              lookup.first_lookup_at,
                            )}`
                          : formatDateTime(lookup.first_lookup_at)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                        {isCarriedOver(lookup) && lookup.lookup_count === 0
                          ? '—'
                          : formatDateTime(lookup.last_lookup_at)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }} align="right">
                        {formatNumber(lookup.lookup_count)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {lookup.first_route ??
                          (isCarriedOver(lookup)
                            ? 'Carried over from monitoring'
                            : '—')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={sourceLabel(lookup.source)}
                          color={SOURCE_COLOUR[lookup.source] ?? 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={windows.pagination.total}
            page={page - 1}
            onPageChange={(_event, newPage) => setPage(newPage + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => {
              setPageSize(parseInt(event.target.value, 10))
              setPage(1)
            }}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            sx={{ '& .MuiTablePagination-spacer': { display: 'none' } }}
          />
        </>
      )}
    </Box>
  )
}

export default CustomerLookups
